
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { useInternalAuth } from '@/hooks/useInternalAuth';

interface InternalLoginFormProps {
  onForgotPassword: () => void;
}

const InternalLoginForm = ({ onForgotPassword }: InternalLoginFormProps) => {
  const { login, loading } = useInternalAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    pin: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Scroll to top to ensure user sees any feedback
    window.scrollTo(0, 0);

    if (!formData.username || !formData.password || !formData.pin) {
      setError('Please fill in all fields');
      return;
    }

    const result = await login(formData.username, formData.password, formData.pin);
    if (!result.success) {
      setError(result.error || 'Login failed');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="username" className="text-white">Username</Label>
        <Input
          id="username"
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          className="bg-gray-900 border-gray-700 text-white"
          placeholder="Enter your username"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-white">Password</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="bg-gray-900 border-gray-700 text-white pr-12"
            placeholder="Enter your password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pin" className="text-white">PIN</Label>
        <div className="relative">
          <Input
            id="pin"
            type={showPin ? 'text' : 'password'}
            value={formData.pin}
            onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
            className="bg-gray-900 border-gray-700 text-white pr-12"
            placeholder="Enter your PIN"
            required
          />
          <button
            type="button"
            onClick={() => setShowPin(!showPin)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
          >
            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-sports-green text-black hover:opacity-90"
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sports-green text-sm hover:underline"
        >
          Forgot password or PIN?
        </button>
      </div>
    </form>
  );
};

export default InternalLoginForm;
